// Global flag to track if the dropdown button has already been created
let dropdownButtonCreated = false

const onUnitsPageMatch = () => {
  // Function to extract propertyHrId from URL
  const getPropertyHrIdFromUrl = () => {
    const url = window.location.href
    const match = url.match(/\/properties\/([^\/]+)\//)
    return match ? match[1] : null
  }

  // Function to fetch property data
  const fetchPropertyData = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('auth'))?.token
      if (!token) throw new Error('Auth Token nicht gefunden.')

      const propertyHrId = getPropertyHrIdFromUrl()
      if (!propertyHrId) {
        throw new Error('Property HR ID nicht in URL gefunden.')
      }

      const response = await fetch(
        `/services/pmp-accounting/api/v1/properties/${propertyHrId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch property data: ${response.statusText}`)
      }

      const propertyData = await response.json()
      console.log('Fetched property data:', propertyData)
      return propertyData
    } catch (error) {
      console.error('Error fetching property data:', error)
      throw error
    }
  }

  // Function to fetch units data
  const fetchUnitsData = async (propertyId) => {
    try {
      const token = JSON.parse(localStorage.getItem('auth'))?.token
      if (!token) throw new Error('Auth Token nicht gefunden.')

      const response = await fetch(
        `/services/pmp-accounting/api/v1/units?propertyId=${propertyId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch units data: ${response.statusText}`)
      }

      const unitsData = await response.json()
      console.log('Fetched units data:', unitsData)
      return unitsData
    } catch (error) {
      console.error('Error fetching units data:', error)
      throw error
    }
  }

  // Function to fetch property owner contracts
  const fetchPropertyOwnerContracts = async (propertyId) => {
    try {
      const token = JSON.parse(localStorage.getItem('auth'))?.token
      if (!token) throw new Error('Auth Token nicht gefunden.')

      const response = await fetch(
        `/services/pmp-accounting/api/v1/contracts?propertyId=${propertyId}&type=PROPERTY_OWNER`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch property owner contracts: ${response.statusText}`)
      }

      const contractsData = await response.json()
      console.log('Fetched property owner contracts:', contractsData)
      return contractsData
    } catch (error) {
      console.error('Error fetching property owner contracts:', error)
      throw error
    }
  }

  // Function to parse property path with array support
  const parsePropertyPath = (propertyPath) => {
    const parts = []
    let current = ''
    let inBrackets = false
    let bracketContent = ''

    for (let i = 0; i < propertyPath.length; i++) {
      const char = propertyPath[i]
      
      if (char === '[') {
        inBrackets = true
        if (current) {
          parts.push({ type: 'property', value: current })
          current = ''
        }
      } else if (char === ']') {
        inBrackets = false
        parts.push({ type: 'index', value: parseInt(bracketContent) })
        bracketContent = ''
      } else if (char === '.') {
        if (!inBrackets) {
          if (current) {
            parts.push({ type: 'property', value: current })
            current = ''
          }
        } else {
          bracketContent += char
        }
      } else {
        if (inBrackets) {
          bracketContent += char
        } else {
          current += char
        }
      }
    }
    
    if (current) {
      parts.push({ type: 'property', value: current })
    }
    
    return parts
  }

  // Function to get value from object using parsed path
  const getValueFromPath = (obj, pathParts) => {
    let value = obj
    
    for (const part of pathParts) {
      if (value && typeof value === 'object') {
        if (part.type === 'property') {
          if (part.value in value) {
            value = value[part.value]
          } else {
            console.warn(`Property ${part.value} not found in object`)
            return undefined
          }
        } else if (part.type === 'index') {
          if (Array.isArray(value) && part.value >= 0 && part.value < value.length) {
            value = value[part.value]
          } else {
            console.warn(`Array index ${part.value} not found or out of bounds`)
            return undefined
          }
        }
      } else {
        console.warn(`Cannot access property on non-object value`)
        return undefined
      }
    }
    
    return value
  }

  // Function to replace template variables
  const replaceTemplateVariables = (
    html,
    propertyData,
    unitsData,
    selectedUnitId = null,
    propertyOwnersData = null
  ) => {
    let processedHtml = html

    // Replace {{property.propertyName}} with actual values
    const propertyRegex = /\{\{property\.([^}]+)\}\}/g
    processedHtml = processedHtml.replace(
      propertyRegex,
      (match, propertyPath) => {
        const pathParts = parsePropertyPath(propertyPath)
        const value = getValueFromPath(propertyData, pathParts)
        
        if (value === undefined) {
          console.warn(`Property path ${propertyPath} not found in property data`)
          return match // Keep original if property not found
        }

        return value !== null ? String(value) : ''
      }
    )

    // Replace {{unit.propertyName}} with actual values from selected unit
    const unitRegex = /\{\{unit\.([^}]+)\}\}/g
    processedHtml = processedHtml.replace(unitRegex, (match, propertyPath) => {
      if (!selectedUnitId || !unitsData) {
        console.warn('No unit selected or units data not available')
        return match // Keep original if no unit selected
      }

      const unitsArray = unitsData.content || unitsData
      if (!unitsArray || !Array.isArray(unitsArray)) {
        console.warn('Units data is not an array')
        return match
      }

      // Try to find the unit with both string and number comparison
      let selectedUnit = unitsArray.find((unit) => unit.id === selectedUnitId)
      
      // If not found, try converting the selectedUnitId to number
      if (!selectedUnit && !isNaN(selectedUnitId)) {
        selectedUnit = unitsArray.find((unit) => unit.id === parseInt(selectedUnitId))
      }
      
      // If still not found, try string comparison
      if (!selectedUnit) {
        selectedUnit = unitsArray.find((unit) => String(unit.id) === selectedUnitId)
      }
      
      if (!selectedUnit) {
        console.warn(`Unit with ID ${selectedUnitId} not found in units array`)
        console.log(
          'Available units:',
          unitsArray.map((u) => ({ id: u.id, name: u.name }))
        )
        return match // Keep original if unit not found
      }

      console.log('Selected unit data:', selectedUnit)

      const pathParts = parsePropertyPath(propertyPath)
      const value = getValueFromPath(selectedUnit, pathParts)
      
      if (value === undefined) {
        console.warn(
          `Property path ${propertyPath} not found in unit data. Available properties:`,
          Object.keys(selectedUnit || {})
        )
        return match // Keep original if property not found
      }

      return value !== null ? String(value) : ''
    })

    // Replace {{propertyOwners}} with actual property owner names
    const propertyOwnersRegex = /\{\{propertyOwners\}\}/g
    processedHtml = processedHtml.replace(propertyOwnersRegex, (match) => {
      if (!propertyOwnersData) {
        console.warn('Property owners data not available')
        return match // Keep original if no owners data
      }

      const contractsArray = propertyOwnersData.content || propertyOwnersData
      if (!contractsArray || !Array.isArray(contractsArray)) {
        console.warn('Property owners data is not an array')
        return match
      }

      // Extract owner names from contracts
      const ownerNames = contractsArray
        .map(contract => contract.mailingContact?.name)
        .filter(name => name) // Filter out undefined/null names

      if (ownerNames.length === 0) {
        console.warn('No owner names found in property owner contracts')
        return match // Keep original if no owners found
      }

      // Join multiple owner names with comma and space
      return ownerNames.join(', ')
    })

    return processedHtml
  }

  const propertyTemplates = [
    {
      name: 'Blanko SEPA',
      html: `
        <style>
          @page {
            margin: 150px 80px 150px 80px; /* top, right, bottom, left */
          }
        </style>
        <div style='max-width: 97%;'>
        <br/>
          <strong>Bitte zurück an:</strong><br/><br/><br/>
          {{property.managementCompany.companyName}}<br/>
          {{property.managementCompany.addresses[0].street}} {{property.managementCompany.addresses[0].number}}<br/>
          {{property.managementCompany.addresses[0].postalCode}} {{property.managementCompany.addresses[0].city}}<br/><br/>
          <hr style="width: 100%; border: 1px solid black; margin: 10px 0;">
          <div style="text-align: right;">{{property.managementCompany.addresses[0].city}}, ${new Date().toLocaleDateString('de-DE')}</div>
        </div>
        <div style='margin-top: 0px; max-width: 97%; text-align: justify;'>
          <span style="font-weight: bold; text-decoration: underline;">SEPA-Lastschriftmandat</span><br/>
<br/>
<table style="width: 100%; margin-bottom: 20px; border-collapse: collapse; font-weight: bold;">
  <tr>
    <td style="padding-right: 20px;">Objekt:</td>
    <td>{{property.name}}</td>
  </tr>
  <tr>
    <td style="padding-right: 20px; vertical-align: top;">c/o:</td>
    <td>{{property.managementCompany.companyName}}<br/>{{property.managementCompany.addresses[0].street}} {{property.managementCompany.addresses[0].number}}, {{property.managementCompany.addresses[0].postalCode}} {{property.managementCompany.addresses[0].city}}</td>
  </tr>
  <tr>
    <td style="padding-right: 20px;">Gläubiger-ID:</td>
    <td>{{property.creditorId}}</td>
  </tr>
  <tr>
    <td style="padding-right: 20px;">Mandatsreferenz:</td>
    <td></td>
  </tr>
  <tr>
    <td style="padding-right: 20px;">Einheitsnummer:</td>
    <td>EH{{unit.unitNrSharingDeclaration}}</td>
  </tr>
  <tr>
    <td style="padding-right: 20px;">Mieter:</td>
    <td></td>
  </tr>
</table>
Ich/Wir ermächtige/n den Eigentümer des Objekts, {{propertyOwners}}, vertreten durch {{property.managementCompany.companyName}}, {{property.managementCompany.addresses[0].street}} {{property.managementCompany.addresses[0].number}}, {{property.managementCompany.addresses[0].postalCode}} {{property.managementCompany.addresses[0].city}}, die fälligen monatlichen Mietzahlungen (Grundmiete und Betriebskosten) und Fehlbeträge aus Jahresabrechnungen 

<br/><br/>
ab ………………………. von folgendem Konto durch SEPA-Lastschrift einzuziehen.
<br/><br/>

Zugleich weise/n ich/wir unser Kreditinstitut an, die vom Eigentümer des Objekts auf unser Konto gezogenen Lastschriften einzulösen.

        <table style="width: 100%; margin-top: 10px; margin-bottom: 20px; border-collapse: collapse;">
          <tr>
            <td style="padding: 10px 0px;">Vorname und Name d. Kontoinhabers</td>
            <td style="padding: 10px 0px;">________________________________</td>
          </tr>
          <tr>
            <td style="padding: 10px 0px;">Kreditinstitut (Name und BIC)</td>
            <td style="padding: 10px 0px;">________________________________</td>
          </tr>
          <tr>
            <td style="padding: 10px 0px;">IBAN:</td>
            <td style="padding: 10px 0px;">________________________________</td>
          </tr>
        </table>
        <div style="margin-top: 40px; display: flex; width: 100%;">
          <div style="width: 50%; text-align: left; display: flex; flex-direction: column; align-items: flex-start;">
            <div style="border-bottom: 1.5px solid #000; width: 80%; margin: 0 0 4px 0;"></div>
            <span>(Ort, Datum)</span>
          </div>
          <div style="width: 50%; text-align: left; display: flex; flex-direction: column; align-items: flex-start;">
            <div style="border-bottom: 1.5px solid #000; width: 80%; margin: 0 0 4px 0;"></div>
            <span>(Unterschrift)</span>
          </div>
        </div>
        <div style="margin-top: 10px;">
          <span>Diese Einzugsermächtigung ist jederzeit schriftlich widerrufbar.</span>
        </div>
        <div style="margin-top: 10px; font-size: 12px;">
          <span>HINWEIS: Wir können innerhalb von 8 Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit unserem Kreditinstitut vereinbarten Bedingungen.</span>
        </div>
        </div>
      `,
    },
  ]

  // Function to create and show watermark selection modal
  const showWatermarkModal = async (template) => {
    try {
      const token = JSON.parse(localStorage.getItem('auth'))?.token
      if (!token) throw new Error('Auth Token nicht gefunden.')

      // Create modal overlay first (non-blocking)
      const modalOverlay = document.createElement('div')
      modalOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      `

      // Create modal content
      const modalContent = document.createElement('div')
      modalContent.style.cssText = `
        background-color: white;
        border-radius: 8px;
        padding: 24px;
        max-width: 500px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        border: 1px solid #000;
      `

      // Create modal header
      const modalHeader = document.createElement('div')
      modalHeader.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e8e8e8;
      `

      const modalTitle = document.createElement('h3')
      modalTitle.textContent = `Briefpapier für "${template.name}" auswählen`
      modalTitle.style.margin = '0'
      modalTitle.style.fontSize = '18px'
      modalTitle.style.fontWeight = '600'
      modalTitle.style.color = '#000'

      const closeButton = document.createElement('button')
      closeButton.innerHTML = '×'
      closeButton.style.cssText = `
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #000;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
      `
      closeButton.addEventListener('click', () => {
        document.body.removeChild(modalOverlay)
      })

      modalHeader.appendChild(modalTitle)
      modalHeader.appendChild(closeButton)

      // Create loading indicator
      const loadingIndicator = document.createElement('div')
      loadingIndicator.style.cssText = `
        text-align: center;
        padding: 20px;
        color: #666;
      `
      loadingIndicator.innerHTML = `
        <div style="margin-bottom: 10px;">Lade Daten...</div>
        <div style="width: 100%; height: 4px; background-color: #f0f0f0; border-radius: 2px; overflow: hidden;">
          <div id="loading-progress" style="width: 0%; height: 100%; background-color: #000; transition: width 0.3s ease;"></div>
        </div>
      `

      // Create watermark selection (initially hidden)
      const watermarkSelection = document.createElement('div')
      watermarkSelection.style.cssText = `
        margin-bottom: 20px;
        display: none;
      `

      const watermarkLabel = document.createElement('label')
      watermarkLabel.textContent = 'Briefpapier auswählen:'
      watermarkLabel.style.cssText = `
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #000;
      `

      const watermarkSelect = document.createElement('select')
      watermarkSelect.style.cssText = `
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #000;
        border-radius: 6px;
        font-size: 14px;
        background-color: white;
        cursor: pointer;
        color: #000;
      `

      watermarkSelection.appendChild(watermarkLabel)
      watermarkSelection.appendChild(watermarkSelect)

      // Create action buttons container (initially hidden)
      const actionButtons = document.createElement('div')
      actionButtons.style.cssText = `
        display: none;
        gap: 12px;
        justify-content: flex-end;
      `

      // Create close button (secondary action)
      const modalCloseButton = document.createElement('button')
      modalCloseButton.textContent = 'Schließen'
      modalCloseButton.style.cssText = `
        background-color: white;
        border: 1px solid #000;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        color: #000;
      `
      modalCloseButton.addEventListener('click', () => {
        document.body.removeChild(modalOverlay)
      })

      // Create download button (primary action)
      const downloadButton = document.createElement('button')
      downloadButton.textContent = 'Herunterladen'
      downloadButton.style.cssText = `
        background-color: #000;
        border: 1px solid #000;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        color: white;
        font-weight: 500;
      `
      downloadButton.addEventListener('mouseenter', () => {
        downloadButton.style.backgroundColor = '#333'
        downloadButton.style.borderColor = '#333'
      })
      downloadButton.addEventListener('mouseleave', () => {
        downloadButton.style.backgroundColor = '#000'
        downloadButton.style.borderColor = '#000'
      })

      actionButtons.appendChild(modalCloseButton)
      actionButtons.appendChild(downloadButton)

      // Assemble modal
      modalContent.appendChild(modalHeader)
      modalContent.appendChild(loadingIndicator)
      modalContent.appendChild(watermarkSelection)
      modalContent.appendChild(actionButtons)
      modalOverlay.appendChild(modalContent)

      // Add modal to page immediately
      document.body.appendChild(modalOverlay)

      // Close modal when clicking outside
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          document.body.removeChild(modalOverlay)
        }
      })

      // Start loading data asynchronously
      const loadData = async () => {
        const progressBar = document.getElementById('loading-progress')
        let propertyData, unitsData, propertyOwnersData, watermarks

        try {
          // Update progress: 25% - Fetching property data
          progressBar.style.width = '25%'
          propertyData = await fetchPropertyData()

          // Update progress: 50% - Fetching units data
          progressBar.style.width = '50%'
          const propertyId = propertyData.id || propertyData.propertyId
          if (!propertyId) {
            throw new Error('Property ID not found in property data')
          }
          unitsData = await fetchUnitsData(propertyId)

          // Update progress: 75% - Fetching property owner contracts
          progressBar.style.width = '75%'
          propertyOwnersData = await fetchPropertyOwnerContracts(propertyId)

          // Update progress: 90% - Fetching watermarks
          progressBar.style.width = '90%'
          const watermarkResponse = await fetch(
            '/services/pmp-document/api/v1/documents?sourceTypes=WATERMARK',
            {
              headers: {
                Authorization: 'Bearer ' + token,
              },
            }
          )

          if (!watermarkResponse.ok) {
            throw new Error('Failed to fetch watermarks')
          }

          const watermarkData = await watermarkResponse.json()
          watermarks = watermarkData.content || []

          // Update progress: 100% - Complete
          progressBar.style.width = '100%'

          // Hide loading indicator and show content
          setTimeout(() => {
            loadingIndicator.style.display = 'none'
            watermarkSelection.style.display = 'block'
            actionButtons.style.display = 'flex'

            // Populate watermark select
            if (watermarks.length === 0) {
              const noWatermarksOption = document.createElement('option')
              noWatermarksOption.value = ''
              noWatermarksOption.textContent = 'Keine Briefpapiere verfügbar'
              noWatermarksOption.disabled = true
              watermarkSelect.appendChild(noWatermarksOption)
            } else {
              // Find default watermark
              let defaultWatermarkIndex = -1
              watermarks.forEach((watermark, index) => {
                try {
                  const metadata = watermark.metaData ? JSON.parse(watermark.metaData) : {}
                  if (metadata.isDefault === true) {
                    defaultWatermarkIndex = index
                  }
                } catch (error) {
                  console.warn('Error parsing watermark metadata:', error)
                }
              })

              watermarks.forEach((watermark, index) => {
                const option = document.createElement('option')
                option.value = watermark.id
                option.textContent = watermark.name
                
                // Select default watermark if found, otherwise select the last item
                if (defaultWatermarkIndex !== -1) {
                  // Select the default watermark
                  if (index === defaultWatermarkIndex) {
                    option.selected = true
                  }
                } else {
                  // Fall back to selecting the last item
                  if (index === watermarks.length - 1) {
                    option.selected = true
                  }
                }
                
                watermarkSelect.appendChild(option)
              })
            }

            // Handle download action
            downloadButton.addEventListener('click', async () => {
              const selectedWatermarkId = watermarkSelect.value

              if (!selectedWatermarkId) {
                alert('Bitte wählen Sie ein Briefpapier aus.')
                return
              }

              // Process template with loaded data
              const unitsArray = unitsData.content || unitsData
              const url = new URL(window.location.href);
              const pathSegments = url.pathname.split('/');
              const unitIndex = pathSegments.indexOf('unit');
              const selectedUnitId = unitIndex !== -1 ? pathSegments[unitIndex + 1] : null;
              const processedHtml = replaceTemplateVariables(
                template.html,
                propertyData,
                unitsData,
                selectedUnitId,
                propertyOwnersData
              )

              // Show loading state
              const originalText = downloadButton.textContent
              downloadButton.textContent = 'Generiere PDF...'
              downloadButton.style.backgroundColor = '#666'
              downloadButton.style.borderColor = '#666'
              downloadButton.style.cursor = 'not-allowed'
              downloadButton.disabled = true

              try {
                const response = await fetch(
                  '/services/pmp-document/api/v1/templates/render',
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + token,
                    },
                    body: JSON.stringify({
                      convertToPdfA: true,
                      convertToPdfA1b: true,
                      displayHeaderFooter: true,
                      html: processedHtml,
                      useLegacyPdfRenderer: false,
                      watermarkId: parseInt(selectedWatermarkId),
                      withPageNumbering: false,
                    }),
                  }
                )

                if (response.ok) {
                  const blob = await response.blob()
                  const url = window.URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `${template.name}.pdf`
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  window.URL.revokeObjectURL(url)

                  // Close modal after successful generation
                  document.body.removeChild(modalOverlay)
                } else {
                  throw new Error('Failed to generate PDF: ' + response.statusText)
                }
              } catch (error) {
                console.error('Error generating PDF:', error)
                alert('Fehler beim Generieren der PDF: ' + error.message)

                // Reset button state
                downloadButton.textContent = originalText
                downloadButton.style.backgroundColor = '#000'
                downloadButton.style.borderColor = '#000'
                downloadButton.style.cursor = 'pointer'
                downloadButton.disabled = false
              }
            })
          }, 300) // Small delay for smooth transition

        } catch (error) {
          console.error('Error loading data:', error)
          loadingIndicator.innerHTML = `
            <div style="color: #d32f2f; margin-bottom: 10px;">Fehler beim Laden der Daten:</div>
            <div style="color: #666; font-size: 14px;">${error.message}</div>
            <button onclick="location.reload()" style="margin-top: 10px; padding: 8px 16px; background-color: #000; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Erneut versuchen
            </button>
          `
        }
      }

      // Start loading data
      loadData()
    } catch (error) {
      console.error('Error showing watermark modal:', error)
      alert('Fehler beim Laden der Briefpapiere: ' + error.message)
    }
  }

  const createDropdownButton = () => {
    // Check if button has already been created
    if (dropdownButtonCreated) {
      console.log('Dropdown button already created, skipping...')
      return
    }

    // Also check if the button already exists in the DOM
    const existingDropdown = document.querySelector('[data-testid="unit-contracts-dropdown"]')
    if (existingDropdown) {
      console.log('Dropdown button already exists in DOM, skipping...')
      dropdownButtonCreated = true
      return
    }

    const closeButton = document.querySelector(
      '[data-testid="unit-contract-history-close-button"]'
    )
    if (!closeButton) return

    const rightSide = closeButton.closest('.right-side')
    if (!rightSide) return

    // Create dropdown container
    const dropdownContainer = document.createElement('div')
    dropdownContainer.setAttribute('data-testid', 'unit-contracts-dropdown')
    dropdownContainer.style.position = 'relative'
    dropdownContainer.style.display = 'inline-block'
    dropdownContainer.style.marginRight = '8px'

    // Create dropdown button
    const dropdownButton = document.createElement('button')
    dropdownButton.type = 'button'
    dropdownButton.className =
      'ant-btn ant-btn-ghost Button ant-dropdown-trigger DropdownButton with-icon'
    dropdownButton.innerHTML =
      '<span>Dokumente</span><span role="img" class="anticon"><svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M0 0L5 5L10 0H0Z" fill="white"></path></svg></span>'
    dropdownButton.style.position = 'relative'

    // Create dropdown menu
    const dropdownMenu = document.createElement('div')
    dropdownMenu.style.position = 'absolute'
    dropdownMenu.style.top = '100%'
    dropdownMenu.style.left = '0'
    dropdownMenu.style.backgroundColor = 'white'
    dropdownMenu.style.border = '1px solid #d9d9d9'
    dropdownMenu.style.borderRadius = '6px'
    dropdownMenu.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'
    dropdownMenu.style.zIndex = '1000'
    dropdownMenu.style.display = 'none'
    dropdownMenu.style.minWidth = '150px'

    // Add options for each template
    propertyTemplates.forEach((template, index) => {
      const option = document.createElement('div')
      option.style.padding = '8px 12px'
      option.style.cursor = 'pointer'
      option.style.borderBottom =
        index < propertyTemplates.length - 1 ? '1px solid #f0f0f0' : 'none'
      option.textContent = template.name
      option.addEventListener('click', async () => {
        console.log('Selected template:', template.name)

        // Show watermark selection modal instead of directly generating PDF
        await showWatermarkModal(template)

        dropdownMenu.style.display = 'none'
      })
      option.addEventListener('mouseenter', () => {
        option.style.backgroundColor = '#f5f5f5'
      })
      option.addEventListener('mouseleave', () => {
        option.style.backgroundColor = 'white'
      })
      dropdownMenu.appendChild(option)
    })

    // Toggle dropdown on button click
    dropdownButton.addEventListener('click', (e) => {
      e.stopPropagation()
      const isVisible = dropdownMenu.style.display === 'block'
      dropdownMenu.style.display = isVisible ? 'none' : 'block'
    })

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!dropdownContainer.contains(e.target)) {
        dropdownMenu.style.display = 'none'
      }
    })

    dropdownContainer.appendChild(dropdownButton)
    dropdownContainer.appendChild(dropdownMenu)
    rightSide.insertBefore(dropdownContainer, closeButton)
    
    // Mark button as created
    dropdownButtonCreated = true
    console.log('Dropdown button created successfully')
  }

  const waitForPageLoad = setInterval(() => {
    const pageContent = document.querySelector('.UnitContractHistoryTable')
    if (pageContent) {
      clearInterval(waitForPageLoad)
      createDropdownButton()
    }
  }, 100)
}
